import { useRef } from 'react';
import { toast } from 'sonner';
import { Camera, FolderOpen, Square, MapPin } from 'lucide-react';
import { compressImage } from '../lib/model';
import { gnssState } from '../lib/gnss';
import DecIn from './DecIn';

export const SCALES = [20, 25, 50, 100, 200, 500];
const dims = (src) => new Promise((res) => { const i = new Image(); i.onload = () => res([i.naturalWidth, i.naturalHeight]); i.onerror = () => res([4, 3]); i.src = src; });
const here = () => new Promise((res) => {
  const g = gnssState();
  if (g.connected && g.lat != null) return res({ lat: g.lat, lon: g.lon, acc: g.hrms, src: 'gnss' });
  if (!navigator.geolocation) return res(null);
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy, src: 'gps' }), () => res(null), { enableHighAccuracy: true, timeout: 15000 });
  return undefined;
});

// scale, background (white / photo / plan) and two control points with photo + position
export default function SketchExtras({ t, sk, setSk }) {
  const cam = useRef(null), file = useRef(null), cpCam = useRef(null), cpFor = useRef(null);
  const bg = async (f) => { if (!f) return; const src = (await compressImage(f, 2400, 0.85)).data, [w, h] = await dims(src); setSk((s) => ({ ...s, bg: { src, w, h, wm: s.bg?.wm || s.W } })); };
  const cpShot = async (f) => {
    const id = cpFor.current; if (!f || !id) return;
    const tid = toast.loading(t.szCpShot);
    const [photo, pos] = await Promise.all([compressImage(f, 1280, 0.8).then((x) => x.data), here()]);
    toast.dismiss(tid);
    if (!pos) toast.error(t.szCpNoPos);
    setSk((s) => ({ ...s, cps: (s.cps || []).map((c) => (c.id === id ? { ...c, photo, ...(pos || {}) } : c)) }));
  };
  return (
    <div className="sz-extras" data-testid="sketch-extras">
      <div className="row">
        <label className="pit-num"><span>{t.szScale}</span><select data-testid="sketch-scale" value={sk.scale || 100} onChange={(e) => setSk({ ...sk, scale: +e.target.value })}>{SCALES.map((n) => <option key={n} value={n}>{`1:${n}`}</option>)}</select></label>
        <b className="muted">{t.szBg}:</b>
        <button type="button" className={`chip seg ${!sk.bg ? 'on' : ''}`} data-testid="sketch-bg-white" onClick={() => setSk({ ...sk, bg: null })}><Square size={13} /> {t.szBgWhite}</button>
        <button type="button" className="chip seg" data-testid="sketch-bg-camera" onClick={() => cam.current.click()}><Camera size={13} /> {t.szBgPhoto}</button>
        <button type="button" className="chip seg" data-testid="sketch-bg-file" onClick={() => file.current.click()}><FolderOpen size={13} /> {t.szBgFile}</button>
        {sk.bg && <label className="pit-num"><span>{t.szBgWidth}</span><DecIn data-testid="sketch-bg-width" value={sk.bg.wm} onNum={(v) => v > 0.2 && setSk({ ...sk, bg: { ...sk.bg, wm: v } })} /></label>}
        <input ref={cam} type="file" hidden accept="image/*" capture="environment" data-testid="sketch-bg-cam-input" onChange={(e) => { bg(e.target.files[0]); e.target.value = ''; }} />
        <input ref={file} type="file" hidden accept="image/*" data-testid="sketch-bg-file-input" onChange={(e) => { bg(e.target.files[0]); e.target.value = ''; }} />
      </div>
      <div className="row sz-cps" data-testid="sketch-cps">
        <b><MapPin size={14} /> {t.szCp}</b>
        {(sk.cps || []).map((c) => (
          <span key={c.id} className="sz-cp">
            {c.photo && <img src={c.photo} alt="" />}
            <b>{c.id}</b> {c.lat != null ? `${c.lat.toFixed(6)}, ${c.lon.toFixed(6)}${c.acc != null ? ` ±${c.acc < 1 ? `${Math.round(c.acc * 100)} cm` : `${Math.round(c.acc)} m`}` : ''}` : t.szCpNone}
            <button type="button" className="btn btn-outline btn-sm" data-testid={`sketch-cp-shot-${c.id}`} onClick={() => { cpFor.current = c.id; cpCam.current.click(); }}><Camera size={13} /> {t.szCpShot}</button>
          </span>
        ))}
        <input ref={cpCam} type="file" hidden accept="image/*" capture="environment" data-testid="sketch-cp-input" onChange={(e) => { cpShot(e.target.files[0]); e.target.value = ''; }} />
        <small className="muted">{t.szCpHint}</small>
      </div>
    </div>
  );
}
